// ── Typed API client for Workflow Automation ───────────────────────

import api from '@/lib/api';

export type TriggerType = 'LEAD_CREATED' | 'LEAD_ASSIGNED' | 'LEAD_STATUS_CHANGED' | 'MEETING_BOOKED' | 'FOLLOWUP_DUE' | 'MEETING_UPCOMING';
export type StepType = 'SEND_WHATSAPP' | 'SEND_EMAIL' | 'SEND_SMS' | 'WAIT' | 'NOTIFY';
export type RecipientType = 'CLIENT' | 'COUNSELOR' | 'ADMIN';

export interface WorkflowStep {
  id?: string;
  order?: number;
  type: StepType;
  recipientType: RecipientType;
  whatsappTemplateId?: string | null;
  emailTemplateId?: string | null;
  smsTemplateId?: string | null;
  delayMinutes?: number;
  condition?: Record<string, unknown>;
  variableMapping?: Record<string, string>;
}

export interface Workflow {
  id: string;
  tenantId: string;
  name: string;
  description: string | null;
  triggerType: TriggerType;
  triggerConfig: Record<string, unknown>;
  isActive: boolean;
  stopOnReply: boolean;
  stopOnConvertOrLost: boolean;
  quietHoursStart: string | null;
  quietHoursEnd: string | null;
  maxPerLeadPerDay: number | null;
  definition: WorkflowDefinition | null;
  createdAt: string;
  updatedAt: string;
  steps: WorkflowStep[];
  _count?: { runs: number };
  stats?: { SENT: number; SKIPPED: number; FAILED: number };
}

// ── v2 visual canvas definition tree ──────────────────────────────
export type RecipientKey = 'Client' | 'Counselor' | 'Admins';
export type ChannelKey = 'WHATSAPP' | 'EMAIL' | 'SMS' | 'NOTIFY' | 'CALL';
export interface RecipCfg {
  on: boolean;
  templateId?: string | null;     // WhatsApp / Email template
  message?: string;               // SMS / Notify inline text
  agentId?: string | null;        // AI voice agent (CALL channel)
  variableMapping?: Record<string, string>;
}
export type DefNode =
  | { kind: 'SEND'; channel: ChannelKey; recipients: Partial<Record<RecipientKey, RecipCfg>> }
  | { kind: 'WAIT'; delayMinutes: number }
  | { kind: 'IF'; cond: { field: string; value?: string }; yes: DefNode[]; no: DefNode[] };
export interface WorkflowDefinition { steps: DefNode[]; }

export interface WorkflowInput {
  name: string;
  description?: string;
  triggerType: TriggerType;
  triggerConfig?: Record<string, unknown>;
  isActive?: boolean;
  stopOnReply?: boolean;
  stopOnConvertOrLost?: boolean;
  quietHoursStart?: string | null;
  quietHoursEnd?: string | null;
  maxPerLeadPerDay?: number | null;
  definition?: WorkflowDefinition;
  steps?: WorkflowStep[];
}

export async function listWorkflows(): Promise<Workflow[]> {
  const res = await api.get('/workflows');
  return Array.isArray(res.data) ? res.data : [];
}

export async function getWorkflow(id: string): Promise<Workflow> {
  const res = await api.get(`/workflows/${id}`);
  return res.data;
}

export async function createWorkflow(body: WorkflowInput): Promise<Workflow> {
  const res = await api.post('/workflows', body);
  return res.data;
}

export async function updateWorkflow(id: string, body: WorkflowInput): Promise<Workflow> {
  const res = await api.patch(`/workflows/${id}`, body);
  return res.data;
}

export async function toggleWorkflow(id: string, isActive: boolean): Promise<Workflow> {
  const res = await api.post(`/workflows/${id}/toggle`, { isActive });
  return res.data;
}

export async function deleteWorkflow(id: string): Promise<void> {
  await api.delete(`/workflows/${id}`);
}

export interface WorkflowRunStepLog {
  stepOrder: number;
  stepType: string;
  recipientType: string;
  status: 'SENT' | 'SKIPPED' | 'FAILED';
  info: string | null;
  createdAt: string;
}

export interface WorkflowRunRow {
  id: string;
  leadId: string;
  leadName: string | null;
  status: 'ACTIVE' | 'COMPLETED' | 'STOPPED' | 'FAILED';
  currentStepOrder: number;
  stoppedReason: string | null;
  startedAt: string;
  completedAt: string | null;
  nextRunAt: string | null;
  steps: WorkflowRunStepLog[];
}

export async function getWorkflowRuns(id: string): Promise<WorkflowRunRow[]> {
  const res = await api.get(`/workflows/${id}/runs`);
  return Array.isArray(res.data) ? res.data : [];
}

// ── Template option helpers (for step pickers) ─────────────────────

export interface TemplateOption {
  id: string;
  label: string;
  /** Number of distinct {{n}} variables in the template BODY (WhatsApp). */
  vars?: number;
  /** BODY text of the template (for preview). */
  body?: string;
}

/** The BODY component text of a Meta template. */
function templateBody(components: any[]): string {
  const body = Array.isArray(components) ? components.find((c) => String(c?.type).toUpperCase() === 'BODY') : null;
  return body?.text ?? '';
}
/** Count distinct {{n}} placeholders in the BODY component of a template. */
function countTemplateVars(components: any[]): number {
  const set = new Set<number>();
  const re = /\{\{(\d+)\}\}/g;
  let m: RegExpExecArray | null;
  const text = templateBody(components);
  while ((m = re.exec(text)) !== null) set.add(Number(m[1]));
  return set.size;
}

/** Approved WhatsApp templates for the SEND_WHATSAPP picker. */
export async function listWhatsappTemplateOptions(): Promise<TemplateOption[]> {
  try {
    const res = await api.get('/whatsapp/templates', { params: { status: 'APPROVED', limit: 100 } });
    const data = Array.isArray(res.data?.data) ? res.data.data : [];
    return data.map((t: any) => ({
      id: t.id,
      label: `${t.name} (${t.language})`,
      vars: countTemplateVars(t.components),
      body: templateBody(t.components),
    }));
  } catch {
    return [];
  }
}

/** Email templates for the SEND_EMAIL picker. */
export async function listEmailTemplateOptions(): Promise<TemplateOption[]> {
  try {
    const res = await api.get('/email-templates');
    const arr = Array.isArray(res.data) ? res.data : Array.isArray(res.data?.data) ? res.data.data : [];
    return arr.map((t: any) => ({ id: t.id, label: t.name || t.subject || t.id }));
  } catch {
    return [];
  }
}
